import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import Select from 'react-select';
import { Row, Col, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';
import debounce from 'lodash/debounce';
import DatePicker from 'react-datepicker';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-datepicker/dist/react-datepicker.css';
import config from '../../config';
import { v4 as uuidv4 } from 'uuid';
import { alertconfirmation } from '../../alerts/Alerts';



const LSACoverage = forwardRef(({ lsaId, renewalCase, isDisableForUsers, initialFormValues = {}, onError = () => { } }, ref) => {
    const [coverageRates, setCoverageRates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const options = [
        { label : "EE",                   value : "EE" },
        { label : "EE+Child",             value : "EE+Child" },
        { label : "EE+Children",          value : "EE+Children" },
        { label : "EE+Child(ren)",        value : "EE+Child(ren)" },
        { label : "EE+Family",            value : "EE+Family" },
        { label : "EE+Spouse",            value : "EE+Spouse" },
        { label : "EE+Domestic Partner",  value : "EE+Domestic Partner" },
        { label : "Spouse+Child",         value : "Spouse+Child" },
        { label : "Spouse+Children",      value : "Spouse+Children" },
        { label : "Spouse+Child(ren)",    value : "Spouse+Child(ren)" },
        { label : "Child Only",           value : "Child Only" },
        { label : "Children Only",        value : "Children Only" },
        { label : "Child(ren) Only",      value : "Child(ren) Only" },
        { label : "Spouse Only",          value : "Spouse Only" },
        { label : "EE+1",                 value : "EE+1" },
        { label : "EE+2",                 value : "EE+2" },
        { label : "EE+3",                 value : "EE+3" },
        { label : "EE+4",                 value : "EE+4" }
    ];


    useEffect(() => {
        const fetchCoverageRates = async () => {
            setLoading(true); // Ensure loading is true before fetching

            try {
                const response = await axios.get(`${config.API_URL}/LSACoverageRates/ByLSAPlanId/${lsaId}`);
                const fetchedCoverageRates = response.data || [];

                // Set coverageRates first
                setCoverageRates(fetchedCoverageRates);

                // if (fetchedCoverageRates.length === 0) {

                //     handleAddPlan();
                // }
            } catch (error) {
                if (error.response?.status !== 404) {
                    setError('Error fetching case files.');
                }
                onError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchCoverageRates();
    }, [lsaId]); // Ensure `lsaId` is the only dependency

    
    const debouncedUpdate = useCallback(
        debounce(async (fieldName, value, id) => {

            try {
                const patchDocument = [
                    { op: 'replace', path: `/${fieldName}`, value: value }
                ];
                const url = `${config.API_URL}/LSACoverageRates/${id}`;
                await axios.patch(url, patchDocument);
            } catch (error) {
                console.error('Error updating field:', error);
                onError(error);
            }

        }, 500),
        [onError]
    );

    const validateField = (name, value, index, updateState = true) => {
        let errors = { ...validationErrors };
        let fieldError = null;

        switch (name) {
            case 'coverageLevelName':
                if (!value) {
                    fieldError = { [name]: 'Pay rate frequency is required.' };
                } else {

                    if (errors[index]) delete errors[index][name];
                }
                break;

            case 'fundingAmount':
                if (isNaN(value) || value === 0) {
                    fieldError = 'Future rate must be a valid number greater than 0.';
                  } else if (value < 0) {
                    fieldError = 'Future rate cannot be negative.';
                  } else if (value > 1000000) {
                    fieldError = 'Future rate is too large. Please enter a number less than 1,000,000.';
                  }
                
                break;

            

            default:
                break;
        }

        if (updateState && fieldError) {
            errors[index] = {
                ...errors[index],
                ...fieldError
            };
            setValidationErrors(errors);
        }

        return fieldError;
    };


    const validateAll = () => {

        let allValid = true;
        let updatedErrors = { ...validationErrors };

        coverageRates.forEach((rate, index) => {

            const requiredFields = [
                'coverageLevelName',
                'fundingAmount',
            ];

            requiredFields.forEach(field => {
                const fieldError = validateField(field, rate[field], index, false);
                if (fieldError) {
                    allValid = false;
                    updatedErrors = {
                        ...updatedErrors,
                        [index]: {
                            ...updatedErrors[index],
                            ...fieldError
                        }
                    };
                }
            });
        });

        setValidationErrors(updatedErrors);
        return allValid;
    };

    useImperativeHandle(ref, () => ({
        validateAll,
    }));

    const handleInputChange = (index, name, value) => {
        validateField(name, value, index);
    
        const updatedCoverageRates = [...coverageRates];
        updatedCoverageRates[index] = { ...updatedCoverageRates[index], [name]: value };
        setCoverageRates(updatedCoverageRates);
    
        debouncedUpdate(name, value, updatedCoverageRates[index].id);
    };

    const handleSelectChange = (index, name, selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        validateField(name, value, index);

        const updatedCoverageRates = [...coverageRates];
        updatedCoverageRates[index] = { ...updatedCoverageRates[index], [name]: value };
        setCoverageRates(updatedCoverageRates);

        debouncedUpdate(name, value, updatedCoverageRates[index].id);
    };

    
    



    const customStyles = (hasError) => ({
        control: (provided) => ({
            ...provided,
            borderColor: hasError ? '#dc3545' : provided.borderColor,
            '&:hover': {
                borderColor: hasError ? '#dc3545' : provided.borderColor,
            },
            boxShadow: hasError ? '0 0 10px rgba(220, 53, 69, 0.5)' : provided.boxShadow,
        }),
    });


    const handleDeletePlan = async (index) => {
        alertconfirmation(
            `Do you want to delete this specific  Pay rate frequency ?`,
            "Confirmation",
            async () => {
                try {
                    const id = coverageRates[index].id;
                    await axios.delete(`${config.API_URL}/LSACoverageRates/${id}`);
                    const updatedCoverageRates = coverageRates.filter((_, i) => i !== index);
                    setCoverageRates(updatedCoverageRates);
                } catch (error) {
                    console.error('Error deleting plan:', error);
                    onError(error);
                }
            }
        );
    };

    const handleAddPlan = async () => {
        try {
            const randomId = uuidv4();


            const newPlan = { ...initialFormValues, id: lsaId+"_"+randomId, lsaPlanId: lsaId };

            const response = await axios.post(`${config.API_URL}/LSACoverageRates`, newPlan);

            const addedSchedule = response.data;
            console.error(addedSchedule);
            setCoverageRates([...coverageRates, addedSchedule]);

        } catch (error) {
            console.error('Error adding new plan:', error);
            onError(error);
        }
    };


const alreadyUpdatedRef = useRef(new Set());


    //const isDisabled = renewalCase?.ebSubmissionStatus === 'Submitted' || isDisableForUsers;
    const isDisabled =  isDisableForUsers;


    return (

        <>
            <h5 id="payScheduleTitle" className="card-title">

            LSA Funding
            </h5>
            <Row class="mb-2">
            <Col lg={3}>Coverage Level</Col>
            <Col lg={3}>
                                Monthly Funding Amount
                            </Col>
                        </Row>
            {coverageRates.map((rate, index) => (
                
                <div key={index}>
                
                    <Row className="mb-2 align-items-center">
                        <Col lg={3}>
                        
                            <Select
                                className={`flex-grow-1 `}
                                title="coverageLevelName"
                                name="coverageLevelName"
                                options={options}
                                value={options?.find(option => option.value === rate.coverageLevelName)}
                                placeholder="Pay Schedule Frequency"
                                onChange={(selectedOption) => handleSelectChange(index, 'coverageLevelName', selectedOption)}
                                styles={customStyles(validationErrors[index] && validationErrors[index].coverageLevelName)}
                                isDisabled={isDisabled}
                            />

                        </Col>
                        <Col lg={3}>
                            
                                <div>

                                
                                <input
                 type="text"
                 className={`form-control ${validationErrors[index] && validationErrors[index].fundingAmount ? 'is-invalid' : ''}`}
                 placeholder="$0.00"
                 name="fundingAmount"
                 value={ rate.fundingAmount }
                 onChange={(e) => handleInputChange(index, e.target.name, e.target.value)}
                 disabled={isDisabled}
                 styles={customStyles(validationErrors[index] && validationErrors[index].fundingAmount)}
               />
               {validationErrors[index] && validationErrors[index].fundingAmount && (
                 <div className="invalid-feedback d-block">
                   {validationErrors[index].fundingAmount}
                 </div>
               )}
                                </div>
                           
                        </Col>
                        <Col lg={2} className="d-flex justify-content-between">
                            {coverageRates.length > 1 && (<button
                                className="delete-plan-info"
                                onClick={() => handleDeletePlan(index)} disabled={isDisabled}
                            >
                                <span className="fa fa-trash-alt me-1" id="delete"></span>
                            </button>)}




                            <button type="button"
                                className="add-coverage ms-4"
                                onClick={handleAddPlan}
                                disabled={isDisabled}
                            >
                                <i className="fa fa-plus"></i>
                            </button>

                        </Col>
                    </Row>
                    
                </div>
            ))}

        </>
    );
});

export default LSACoverage;


